import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef } from "@angular/core";
import { AdminService } from "@mypxplat/xplat/core";
import { AppService } from "@mypxplat/xplat/web/core";
import { TranslateService } from "@ngx-translate/core";
import { SkuFoundConfirmComponent } from "../../modals";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "myp-missing-serial-item",
  templateUrl: "missing-serial-item.component.html",
  standalone: false,
  styles: [
    `
      .unviewable-photo {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        min-height: 140px;
        padding: 10px;
      }
      .unviewable-photo .break-word {
        overflow-wrap: anywhere;
      }
    `,
  ],
})
export class MissingSerialItemComponent {
  @Input() ms: any;
  @Input() collapsible: boolean = true;
  @Input() showUserId: boolean = true;
  @Input() savingSku: boolean = false;
  @Input() converting: boolean = false;

  @Output() onSaveSku = new EventEmitter<any>();
  @Output() onSerialUpdated = new EventEmitter<any>();
  @Output() onConverted = new EventEmitter<any>();
  @Output() onConvert = new EventEmitter<any>();
  @Output() onDelete = new EventEmitter<any>();
  @Output() onShowImage = new EventEmitter<string>();

  public savingSerial: boolean = false;

  /**
   * A photo the browser couldn't decode — HEIC from an iPhone is the common case.
   * The filename is resolved here rather than in the template so change detection
   * isn't re-running it every cycle, and so support can see what they're about to
   * download in place of the broken image.
   */
  markPhotoFailed(ms: any, which: "body" | "serial") {
    if (which === "body") {
      ms.bodyImageFailed = true;
      ms.bodyImageFileName = this.photoFileName(ms.bodyImageUrl);
    } else {
      ms.serialImageFailed = true;
      ms.serialImageFileName = this.photoFileName(ms.serialImageUrl);
    }
  }

  /** Last path segment of a photo URL. */
  photoFileName(url: string): string {
    if (!url) return "";
    try {
      const path = new URL(url, window.location.origin).pathname;
      return decodeURIComponent(path.split("/").pop() || url);
    } catch {
      return url.split("?")[0].split("/").pop() || url;
    }
  }

  constructor(
    private adminService: AdminService,
    private appService: AppService,
    private _translate: TranslateService,
    private _cdr: ChangeDetectorRef
  ) {}

  updateSerial(ms) {
    if (this.savingSerial) return;
    this.savingSerial = true;
    this.adminService
      .put(`product/fender-gear/update-missing-serial`, {
        userId: ms.userId,
        oldSerial: ms.serial,
        newSerial: ms.editingSerial,
      })
      .subscribe({
        next: (result: any) => {
          this.savingSerial = false;
          ms.serial = result.updatedRecord.serial;
          ms.showingEditSerial = false;
          ms.editingSerial = undefined;
          if (result.fenderGear) {
            ms.sku = String(result.fenderGear.sku);
            this.showSkuFoundModal(ms, result.fenderGear);
          } else {
            this.onSerialUpdated.emit(ms);
          }
          this._cdr.markForCheck();
        },
        error: (error) => {
          this.savingSerial = false;
          this._cdr.markForCheck();
          this.appService.alert(error?.error?.message || this._translate.instant("products.register.missing_fender_serial.update_serial_failed"));
        },
      });
  }

  private showSkuFoundModal(ms: any, fenderGear: any) {
    const sku = String(fenderGear.sku);
    this.adminService.searchFenderGear(sku).subscribe({
      next: (result: any) => {
        const product = result?.snapshot?.product;
        this.openSkuFoundModal(ms, {
          sku,
          imageUrl: product?.imageUrl || fenderGear.image,
          productTitle: product?.title,
        });
      },
      error: () => {
        this.openSkuFoundModal(ms, {
          sku,
          imageUrl: fenderGear.image,
        });
      },
    });
  }

  private openSkuFoundModal(ms: any, display: { sku: string; imageUrl?: string; productTitle?: string }) {
    const modalRef = this.appService.showModal(SkuFoundConfirmComponent, { size: "md" });
    modalRef.componentInstance.sku = display.sku;
    modalRef.componentInstance.imageUrl = display.imageUrl;
    modalRef.componentInstance.productTitle = display.productTitle;
    modalRef.componentInstance.submitFn = () => this.confirmConvert(ms);
  }

  confirmConvert(ms) {
    this.adminService.put("product/fender-gear/missing-serials/convert", { serial: ms.serial, sku: ms.sku, userId: ms.userId, confirmed: true }).subscribe({
      next: () => {
        this.appService.alert(this._translate.instant("products.register.missing_fender_serial.convert_success"));
        this.onConverted.emit(ms);
      },
      error: (error) => {
        this.appService.alertError(error);
      },
    });
  }
}
